<template>
  <v-container class="modern-master-view pb-12">
    <!-- Global Header (Master Pattern) -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-exchange-alt</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Libro de Movimientos</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Historial completo de entradas, salidas y ajustes de stock</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center gap-3">
        <export-transactions
          :filter="filter"
          :options="options"
          :disabled="loading"
          :search="search"
        />
      </div>
    </v-row>

    <!-- Filters Section -->
    <v-card outlined class="modern-card shadow-sm pa-6 mb-8">
      <v-row dense>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1 px-1">Creada Desde</div>
          <v-text-field 
            v-model="filter.created_after" 
            type="date" 
            outlined dense hide-details 
            class="modern-input" 
            :disabled="loading"
            @change="()=>{options.page=1;fetch()}"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1 px-1">Creada Hasta</div>
          <v-text-field 
            v-model="filter.created_before" 
            type="date" 
            outlined dense hide-details 
            class="modern-input" 
            :disabled="loading"
            @change="()=>{options.page=1;fetch()}"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1 px-1">Tipo de Movimiento</div>
          <v-select
            v-model="filter.tipo"
            :items="tipos"
            item-text="name"
            item-value="id"
            placeholder="Todos los Tipos"
            outlined dense hide-details
            class="modern-input"
            clearable
            :disabled="loading"
            @change="()=>{options.page=1;fetch()}"
            prepend-inner-icon="fas fa-filter"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase mb-1 ml-1 px-1">Bodega / Almacén</div>
          <v-select
            v-model="filter.store"
            :items="stores"
            item-text="name"
            item-value="id"
            placeholder="Todas las Bodegas"
            outlined dense hide-details
            class="modern-input"
            clearable
            :disabled="loading"
            @change="()=>{options.page=1;fetch()}"
            prepend-inner-icon="fas fa-warehouse"
          />
        </v-col>
      </v-row>
    </v-card>

    <!-- Data Table Section -->
    <v-card outlined class="modern-card shadow-sm overflow-hidden">
      <v-data-table
        :headers="headers"
        :items="transactions"
        :options.sync="options"
        :server-items-length="parseInt(totalItems)"
        :loading="loading"
        class="modern-table"
        @update:options="fetch"
        sort-by="created_at"
        :item-class="itemRowStyle"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
      >
        <!-- Table Top Slot: Integrated Search -->
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom bg-lightgrey d-flex align-center">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Registros Encontrados: {{totalItems}}</span>
            <v-spacer></v-spacer>
            <v-text-field
              v-model="search"
              prepend-inner-icon="fas fa-search"
              placeholder="Buscar por artículo, usuario o referencia..."
              outlined dense hide-details
              class="modern-search-field"
              @keyup="searchTimeOut"
              :disabled="loading"
            />
          </div>
        </template>

        <template v-slot:item.created_at="{ item }">
          <div class="caption font-weight-bold grey--text">{{item.created_at | moment('DD/MM/YYYY')}}</div>
        </template>

        <template v-slot:item.id="{ item }">
          <v-chip x-small label class="font-weight-black px-2">#{{item.id}}</v-chip>
        </template>

        <template v-slot:item.article_name="{ item }">
          <div class="d-flex flex-column">
            <span class="caption font-weight-black primary--text lh-1 mb-1">[{{item.article_sku}}]</span>
            <span class="body-2 font-weight-medium grey--text text--darken-3">{{item.article_name}}</span>
          </div>
        </template>

        <template v-slot:item.created_by_full_name="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="24" color="grey lighten-3" class="mr-2">
              <span class="caption primary--text font-weight-bold">{{item.created_by_full_name.charAt(0)}}</span>
            </v-avatar>
            <span class="caption font-weight-medium">{{item.created_by_full_name}}</span>
          </div>
        </template>

        <template v-slot:item.notes="{ item }">
          <div class="caption grey--text text--darken-1 font-italic">{{item.notes || '—'}}</div>
        </template>

        <template v-slot:item.tipo="{ item }">
          <div class="d-flex align-center">
            <v-icon x-small :color="item.tipo_in_out ? 'success' : 'error'" class="mr-2">
              {{item.tipo_in_out ? 'fas fa-arrow-right-to-bracket' : 'fas fa-arrow-right-from-bracket'}}
            </v-icon>
            <span class="caption font-weight-black text-uppercase">{{item.tipo_name}}</span>
          </div>
        </template>

        <template v-slot:item.qty="{ item }">
          <div :class="item.tipo_in_out ? 'success--text' : 'error--text'" class="font-weight-black">
            {{item.tipo_in_out ? '+' : '-'}}{{item.qty | number('0,0.00')}}
          </div>
        </template>

        <template v-slot:item.price="{ item }">
          <span class="caption font-weight-bold">{{item.price | moneda}}</span>
        </template>

        <template v-slot:item.total="{ item }">
          <span :class="item.tipo_in_out ? 'success--text' : 'error--text'" class="font-weight-black">
            {{ (item.tipo_in_out ? 1 : -1) * item.price * item.qty | moneda }}
          </span>
        </template>

        <template v-slot:item.actions="{ item }">
          <consume-by-o-t v-if="!item.tipo_in_out" :article="item.article" :transaction="item" />
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>
<script>
import {fetchTransactions, fetchTipos} from '@/api/stocks'
import ExportTransactions from '@/components/stocks/ExportTransactions.vue'
import ConsumeByOT from '@/components/stocks/ConsumeByOT.vue'
import moment from 'moment'
export default{
    name: 'Transactions',
    components:{
      ExportTransactions,
      ConsumeByOT
    },
    async mounted(){
     this.tipos=await fetchTipos()
     this.fetch()
    },
    computed:{
        stores(){
          return this.$store.getters.getStores
        }
    },
    data(){
        return{
            headers:[
            { text: 'Creada', value: 'created_at' ,class:'table-header-2',sortable:false,},
            {text:'Nro.',value:'id',class:'table-header-2',sortable:false,},
            { text: 'Usuario', value: 'created_by_full_name' ,class:'table-header-2',sortable:false,},
            { text: 'Articulo', value: 'article_name' ,class:'table-header-2',sortable:false,},
            { text: 'Ref.', value: 'notes',class:'table-header-2',sortable:false},
            { text: 'Bodega', value: 'store_name',class:'table-header-2',sortable:false},
            { text: 'Movimiento', value: 'tipo',class:'table-header-2' ,sortable:false,width:'10%'},
            { text: 'Cant.', value: 'qty',class:'table-header-2',sortable:false,align:'end' },
            { text: 'Valor', value: 'price',class:'table-header-2',sortable:false,align:'end'},
            { text: 'Total', value: 'total',class:'table-header-2',sortable:false,align:'end'},
            { text: '', value: 'actions',class:'table-header-2',sortable:false,align:'end'},
            ],
            tipos:[],
            transactions:[],
            transactionLoading:false,
            search:'',
            filter:{
                created_after:moment(new Date(new Date().getFullYear(), new Date().getMonth(), 1)).format('yyyy-MM-DD'),
                created_before:'',
                store:'',
                tipo:'',
            },
            loading:false,
            totalItems:0,
            timer:''

        }
    },
    methods:{
        async fetch(){
            this.loading=true
            let sort=''
            if(!this.filter.created_after) this.filter.created_after=''
            if(!this.filter.created_before) this.filter.created_before=''
            if (!this.filter.store) this.filter.store=''
            const {sortBy, sortDesc, page, itemsPerPage }=this.options
            sort=sortBy[0]
            const query=`ordering=${sortDesc[0]?sort:'-' + sort}&page=${page}&page_size=${itemsPerPage}&store=${this.filter.store}&created_after=${this.filter.created_after}&created_before=${this.filter.created_before}&search=${this.search}&tipo=${this.filter.tipo}`
            await fetchTransactions(query)
            .then(res=>{
                this.totalItems=res.count
                this.transactions=res.results.map(r=>{
                  r.article.consume=r.consume
                  return r
                })
                this.loading=false
            })
        },
        searchTimeOut() {  
            //Función para hacer búsqueda lazy
            if (this.timer) {
                clearTimeout(this.timer);
                this.timer = null;
            }
            this.timer = setTimeout(() => {
                this.options.page=1
                this.fetch()
            }, 1000);
        },
        itemRowStyle(item){
            return item.tipo_in_out&&item.tipo_price_effect ? 'font-weight-black':''
        },
    }
}

</script>

<style scoped>
.modern-master-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.shadow-sm { box-shadow: 0 2px 12px rgba(0,0,0,0.04) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 2px; }

.modern-input >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-input >>> .v-input__control { background-color: white !important; }

.modern-search-field { width: 320px; }
.modern-search-field >>> .v-input__control fieldset { border-radius: 24px !important; border-color: #e0e4e8 !important; background-color: white !important; }

.modern-table >>> .v-data-table-header th { white-space: nowrap; height: 52px !important; text-transform: uppercase; font-size: 0.65rem !important; letter-spacing: 1.5px; font-weight: 900 !important; background-color: #f8fbfc !important; color: #607d8b !important; }
.modern-table >>> td { padding: 12px 16px !important; border-bottom: 1px solid #f0f3f5 !important; }
.modern-table >>> tr:hover { background-color: rgba(var(--v-primary-base), 0.02) !important; }

.v-avatar.border-white-sm { border: 2px solid white !important; }
</style>